import React, { useEffect, useState } from "react";
import {
  EuiBadge,
  EuiButton,
  EuiCallOut,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiHorizontalRule,
  EuiPanel,
  EuiSpacer,
  EuiText,
  EuiTextArea,
  EuiTitle,
} from "@elastic/eui";
import { useApi } from "../";
import { useAnketaAuth } from "../auth/context";
import { useAnketaI18n } from "src/internationalization";
import { TableSkeleton } from "src/components/skeleton";

export interface ManagedSetting {
  key: string;
  label: string;
  /** Name of the environment variable behind this setting. */
  env: string;
  /** Which admin screen owns this field, so it is rendered exactly once. */
  group: string;
  /** The stored override ("" when none). */
  stored: string;
  /** What `.env` says. */
  envDefault: string;
  /** The compiled-in default behind the environment ("" when there is none). */
  builtInDefault: string;
  /** "url-list" settings may hold several values and get a multi-line field. */
  kind?: string;
  /** What is actually in force. */
  effective: string;
  source: "database" | "environment" | "default" | "unset";
}

/**
 * Editor for the admin-managed runtime settings (superuser-only).
 *
 * The model the UI has to make legible: **`.env` supplies the default, a saved
 * value overrides it, and clearing the field resets to the default.** An empty
 * input is therefore not "nothing configured" - it means "whatever the
 * environment says" - so each row states which of the two is in force and shows
 * the inherited value as the placeholder rather than leaving the operator to
 * guess.
 *
 * Saving PUTs the changed keys to /api/v2/admin/settings; the backend validates
 * against its allowlist and deletes rows for blank values.
 */
export const AdminSettings = ({
  group = "general",
}: {
  /**
   * Which settings to render. Each setting declares its group server-side, so a
   * value appears on exactly one screen - the same field in two tabs is a field
   * someone changes in one and does not notice in the other.
   */
  group?: string;
} = {}): JSX.Element => {
  const { Get, Put } = useApi();
  const { keycloak } = useAnketaAuth();
  const { translate } = useAnketaI18n();

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<boolean>(false);

  const [settings, setSettings] = useState<ManagedSetting[]>([]);
  /** Edited overrides, keyed by setting. Absent = untouched. */
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const load = async () => {
    setLoading(true);
    setError(null);
    const resp = await Get<any>({
      path: "v2/admin/settings",
      responseType: "json",
      token: keycloak?.token,
    });
    // useApi resolves with success:false on an HTTP error rather than throwing,
    // so the check has to be explicit - a try/catch here would be dead code.
    const payload = resp?.success ? ((resp?.data as any)?.data ?? null) : null;
    if (!payload?.settings) {
      setError(
        translate("anketa.admin.settings.load.error", {
          defaultMessage: "Settings could not be loaded.",
        }),
      );
      setLoading(false);
      return;
    }
    setSettings(
      (payload.settings as ManagedSetting[]).filter((s) => s.group === group),
    );
    setDrafts({});
    setLoading(false);
  };

  useEffect(() => {
    if (keycloak?.token) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token, group]);

  const valueOf = (setting: ManagedSetting): string =>
    drafts[setting.key] ?? setting.stored;

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    const resp = await Put<any>({
      path: "v2/admin/settings",
      responseType: "json",
      token: keycloak?.token,
      // Only what was actually edited, so an untouched row is never rewritten
      // (and never audit-logged as a change).
      value: { settings: drafts },
    });
    if (resp?.success) {
      setSaved(true);
      await load();
    } else {
      const details: string[] | undefined = (resp?.data as any)?.data?.details;
      setError(
        details?.length
          ? details.join(" · ")
          : translate("anketa.admin.save.error", {
              defaultMessage: "Saving failed.",
            }),
      );
    }
    setSaving(false);
  };

  if (loading) {
    return <TableSkeleton rows={4} />;
  }

  const dirty = Object.keys(drafts).length > 0;

  if (settings.length === 0 && !error) {
    return (
      <EuiText size="s" color="subdued">
        {translate("anketa.admin.settings.emptyGroup", {
          defaultMessage: "No settings in this section.",
        })}
      </EuiText>
    );
  }

  return (
    <>
      <EuiText size="s" color="subdued">
        {translate("anketa.admin.settings.description", {
          defaultMessage:
            "Runtime settings. Each one falls back to its environment variable; a value saved here overrides it, and clearing the field resets it to the environment default. Changes take effect immediately - no redeploy.",
        })}
      </EuiText>
      <EuiSpacer size="m" />

      {error && (
        <>
          <EuiCallOut title={error} color="danger" iconType="alert" />
          <EuiSpacer size="m" />
        </>
      )}
      {saved && (
        <>
          <EuiCallOut
            title={translate("anketa.admin.settings.saved", {
              defaultMessage: "Settings saved.",
            })}
            color="success"
            iconType="check"
          />
          <EuiSpacer size="m" />
        </>
      )}

      {settings.map((setting) => (
        <React.Fragment key={setting.key}>
          <EuiPanel hasShadow={false} hasBorder paddingSize="m">
            <EuiFlexGroup alignItems="center" justifyContent="spaceBetween">
              <EuiFlexItem grow={false}>
                <EuiTitle size="xxs">
                  <h4>{setting.label}</h4>
                </EuiTitle>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                {/* Which of the two sources is actually in force. Without this an
                    empty field reads as "not configured" when the environment
                    may well be supplying a working value. */}
                {setting.source === "database" ? (
                  <EuiBadge color="accent">
                    {translate("anketa.admin.settings.source.database", {
                      defaultMessage: "Managed here",
                    })}
                  </EuiBadge>
                ) : setting.source === "environment" ? (
                  <EuiBadge color="hollow">
                    {translate("anketa.admin.settings.source.environment", {
                      values: { env: setting.env },
                      defaultMessage: "From {{env}}",
                    })}
                  </EuiBadge>
                ) : setting.source === "default" ? (
                  /* Neither stored nor in the environment, but not unset
                     either - saying "Not configured" here would send someone
                     hunting for a variable that does not exist. */
                  <EuiBadge color="default">
                    {translate("anketa.admin.settings.source.default", {
                      defaultMessage: "Built-in default",
                    })}
                  </EuiBadge>
                ) : (
                  <EuiBadge color="warning">
                    {translate("anketa.admin.settings.source.unset", {
                      defaultMessage: "Not configured",
                    })}
                  </EuiBadge>
                )}
              </EuiFlexItem>
            </EuiFlexGroup>

            <EuiSpacer size="s" />

            <EuiFormRow
              fullWidth
              label={translate("anketa.admin.settings.override.label", {
                defaultMessage: "Override",
              })}
              helpText={
                setting.kind === "url-list"
                  ? translate("anketa.admin.settings.override.helpList", {
                      values: { env: setting.env },
                      defaultMessage:
                        "One or more NetBox device-type URLs, separated by commas or line breaks. All of them count for this hardware kind; the first one is what a hardware order quotes. Leave empty to use {{env}} or the built-in default.",
                    })
                  : setting.envDefault
                  ? translate("anketa.admin.settings.override.help", {
                      values: { env: setting.env },
                      defaultMessage:
                        "Leave empty to use {{env}} from the environment.",
                    })
                  : setting.builtInDefault
                    ? translate("anketa.admin.settings.override.helpDefault", {
                        values: {
                          env: setting.env,
                          value: setting.builtInDefault,
                        },
                        defaultMessage:
                          "{{env}} is not set, so leaving this empty uses the built-in default {{value}}.",
                      })
                    : translate("anketa.admin.settings.override.helpNoEnv", {
                        values: { env: setting.env },
                        defaultMessage:
                          "{{env}} is not set in the environment, so this is the only source.",
                      })
              }
            >
              {/*
                A list setting holds several URLs - two DAC device types, two
                server generations - which do not fit a single-line field in
                any readable way.
              */}
              {setting.kind === "url-list" ? (
                <EuiTextArea
                  fullWidth
                  compressed
                  rows={3}
                  value={valueOf(setting)}
                  placeholder={
                    setting.envDefault ||
                    setting.builtInDefault ||
                    translate("anketa.admin.settings.override.empty", {
                      defaultMessage: "Not configured",
                    })
                  }
                  onChange={(e) => {
                    setSaved(false);
                    setDrafts((prev) => ({
                      ...prev,
                      [setting.key]: e.target.value,
                    }));
                  }}
                />
              ) : (
              <EuiFieldText
                fullWidth
                value={valueOf(setting)}
                // The inherited value as the placeholder, so it is visible
                // without being mistaken for an override that has been saved.
                placeholder={
                  setting.envDefault ||
                  setting.builtInDefault ||
                  translate("anketa.admin.settings.override.empty", {
                    defaultMessage: "Not configured",
                  })
                }
                onChange={(e) => {
                  setSaved(false);
                  setDrafts((prev) => ({
                    ...prev,
                    [setting.key]: e.target.value,
                  }));
                }}
              />
              )}
            </EuiFormRow>
          </EuiPanel>
          <EuiSpacer size="m" />
        </React.Fragment>
      ))}

      <EuiHorizontalRule />

      <EuiButton fill onClick={save} isLoading={saving} isDisabled={!dirty}>
        {translate("anketa.admin.save.button", { defaultMessage: "Save" })}
      </EuiButton>{" "}
      <EuiButton onClick={load} isDisabled={saving}>
        {translate("anketa.admin.reset.button", { defaultMessage: "Reset" })}
      </EuiButton>
    </>
  );
};
